import { SIGNED_OUT_EVENT } from '@/utils/signOutStorage';

// v6 adds prepTimeMinutes to the cached rows; older entries would render the
// fallback ETA until they expired.
const CACHE_PREFIX = 'phantom:restaurant-discovery:v6:';
const CACHE_TTL_MS = 60 * 1000;

const memoryCache = new Map();

// The rows are keyed by the coach's delivery address; the next person in this
// tab must not inherit them (signOutStorage clears the sessionStorage copies).
if (typeof window !== 'undefined') {
  window.addEventListener(SIGNED_OUT_EVENT, () => memoryCache.clear());
}

const normalizeCacheKeyPart = (value) => String(value || '').trim().toLowerCase();

const buildCoordinateKey = (coords = null) => {
  if (coords?.lat == null || coords?.lng == null) return '';
  return `${Number(coords.lat).toFixed(4)},${Number(coords.lng).toFixed(4)}`;
};

// priceTiers: the tiers a price-filtered search asked the provider for
// ("1,2"); its rows are another list than the unfiltered one. Left out of
// the key when empty, so unfiltered entries keep their keys.
const buildCacheKey = ({
  query = '',
  fulfillmentType = '',
  address = '',
  coords = null,
  radiusMiles = '',
  requestedTime = '',
  priceTiers = '',
} = {}) =>
  [
    normalizeCacheKeyPart(fulfillmentType),
    normalizeCacheKeyPart(query),
    normalizeCacheKeyPart(address),
    buildCoordinateKey(coords),
    normalizeCacheKeyPart(radiusMiles),
    normalizeCacheKeyPart(requestedTime),
    ...(normalizeCacheKeyPart(priceTiers) ? [`tiers:${normalizeCacheKeyPart(priceTiers)}`] : []),
  ].join('::');

const getStorageKey = (cacheKey) => `${CACHE_PREFIX}${cacheKey}`;

const isFresh = (entry, maxAgeMs = CACHE_TTL_MS) =>
  Boolean(entry?.timestamp) && (Date.now() - Number(entry.timestamp)) <= maxAgeMs;

const readSessionEntry = (cacheKey, maxAgeMs = CACHE_TTL_MS) => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.sessionStorage.getItem(getStorageKey(cacheKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isFresh(parsed, maxAgeMs) || !Array.isArray(parsed?.rows)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeSessionEntry = (cacheKey, entry) => {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(getStorageKey(cacheKey), JSON.stringify(entry));
  } catch {
    // Ignore storage quota or privacy-mode failures.
  }
};

// maxAgeMs: how old an entry may be. Search results expire after
// CACHE_TTL_MS; a caller after the stores themselves (not their live ETAs)
// can accept an older list.
export const readPhantomRestaurantCache = ({
  query = '',
  fulfillmentType = '',
  address = '',
  coords = null,
  radiusMiles = '',
  requestedTime = '',
  priceTiers = '',
  maxAgeMs = CACHE_TTL_MS,
} = {}) => {
  const cacheKey = buildCacheKey({ query, fulfillmentType, address, coords, radiusMiles, requestedTime, priceTiers });
  const inMemory = memoryCache.get(cacheKey);
  if (isFresh(inMemory, maxAgeMs) && Array.isArray(inMemory?.rows)) {
    return inMemory.rows;
  }

  const inSession = readSessionEntry(cacheKey, maxAgeMs);
  if (inSession) {
    memoryCache.set(cacheKey, inSession);
    return inSession.rows;
  }

  return null;
};

export const writePhantomRestaurantCache = (
  rows = [],
  {
    query = '',
    fulfillmentType = '',
    address = '',
    coords = null,
    radiusMiles = '',
    requestedTime = '',
    priceTiers = '',
  } = {},
) => {
  const cacheKey = buildCacheKey({ query, fulfillmentType, address, coords, radiusMiles, requestedTime, priceTiers });
  const entry = {
    timestamp: Date.now(),
    rows: Array.isArray(rows) ? rows : [],
  };

  memoryCache.set(cacheKey, entry);
  writeSessionEntry(cacheKey, entry);
};

// allowStale: ignore the freshness TTL. Use this when hydrating identity/image for
// the detail page — a restaurant's name/image don't expire the way search pricing
// does, and ignoring the TTL prevents the hero from blanking on a refresh >60s after
// the snapshot was cached. Live pricing/availability still come from get_menu.
export const findCachedPhantomRestaurantSnapshot = (restaurantId, { allowStale = false } = {}) => {
  const normalizedRestaurantId = normalizeCacheKeyPart(restaurantId);
  if (!normalizedRestaurantId) return null;

  const matchesRow = (row) => {
    const providerRestaurantId = normalizeCacheKeyPart(row?.provider_restaurant_ids?.mcp);
    const rowId = normalizeCacheKeyPart(row?.id);
    return rowId === normalizedRestaurantId || providerRestaurantId === normalizedRestaurantId;
  };

  for (const entry of memoryCache.values()) {
    if ((!allowStale && !isFresh(entry)) || !Array.isArray(entry?.rows)) continue;
    const match = entry.rows.find(matchesRow);
    if (match) return match;
  }

  if (typeof window === 'undefined') return null;

  try {
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (!key || !key.startsWith(CACHE_PREFIX)) continue;
      const raw = window.sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if ((!allowStale && !isFresh(parsed)) || !Array.isArray(parsed?.rows)) continue;
      const match = parsed.rows.find(matchesRow);
      if (match) {
        const cacheKey = key.slice(CACHE_PREFIX.length);
        memoryCache.set(cacheKey, parsed);
        return match;
      }
    }
  } catch {
    return null;
  }

  return null;
};
